// src/games/Rebus/rules.ts
// Regras puras do rébus (contrato P0): 5 pontos por posição certa; posições
// reveladas (índices 0-based em `solucao`, contando espaços) não pontuam.
export const POINTS_PER_POSITION = 5;

export interface RebusAnswer {
  input: string;
  reveladas: number[];
}

const upper = (value: string | undefined) => (value ?? '').toUpperCase();

export function normalizeReveladas(value: unknown, length?: number): number[] {
  if (!Array.isArray(value)) return [];
  const valid = value.filter(
    (n): n is number => Number.isInteger(n) && n >= 0 && (length === undefined || n < length)
  );
  return Array.from(new Set(valid)).sort((a, b) => a - b);
}

export function rebusCorrectCount(solucao: string, input: string): number {
  let total = 0;
  for (let i = 0; i < solucao.length; i++) {
    if (input[i] !== undefined && upper(input[i]) === upper(solucao[i])) total += 1;
  }
  return total;
}

/** Contrato: 5 × índices com upper(input[i]) == upper(solucao[i]) e i ∉ reveladas. */
export function rebusPoints(solucao: string, input: string, reveladas: number[]): number {
  const revealed = new Set(reveladas);
  let total = 0;
  for (let i = 0; i < solucao.length; i++) {
    if (!revealed.has(i) && input[i] !== undefined && upper(input[i]) === upper(solucao[i])) total += 1;
  }
  return total * POINTS_PER_POSITION;
}

export function rebusMaxPoints(solucao: string): number {
  return solucao.length * POINTS_PER_POSITION;
}

/** Primeira posição diferente da resposta; -1 quando está tudo certo. */
export function firstMismatch(solucao: string, input: string): number {
  for (let i = 0; i < solucao.length; i++) {
    if (upper(input[i]) !== upper(solucao[i])) return i;
  }
  return -1;
}

function withChars(input: string, start: number, chars: string): string {
  const padded = input.length < start ? input.padEnd(start, ' ') : input;
  return padded.slice(0, start) + chars + padded.slice(start + chars.length);
}

export function revealRebusLetter(solucao: string, answer: RebusAnswer): RebusAnswer {
  const i = firstMismatch(solucao, answer.input);
  if (i < 0) return answer;
  return {
    input: withChars(answer.input, i, solucao[i]),
    reveladas: normalizeReveladas([...answer.reveladas, i], solucao.length),
  };
}

export function revealRebusWord(solucao: string, answer: RebusAnswer): RebusAnswer {
  const i = firstMismatch(solucao, answer.input);
  if (i < 0) return answer;
  let start = i;
  let end: number;
  if (solucao[i] === ' ') {
    // Contrato: posição errada/vazia é um ESPAÇO → escreve e marca o espaço
    // e a palavra inteira que vem depois dele.
    end = i + 1;
    while (end < solucao.length && solucao[end] !== ' ') end += 1;
  } else {
    while (start > 0 && solucao[start - 1] !== ' ') start -= 1;
    end = i;
    while (end < solucao.length && solucao[end] !== ' ') end += 1;
  }
  const positions = Array.from({ length: end - start }, (_, k) => start + k);
  return {
    input: withChars(answer.input, start, solucao.slice(start, end)),
    reveladas: normalizeReveladas([...answer.reveladas, ...positions], solucao.length),
  };
}

export function revealRebusAll(solucao: string): RebusAnswer {
  return { input: solucao, reveladas: Array.from({ length: solucao.length }, (_, i) => i) };
}

/**
 * "Limpar" (como o app): apaga TODA a resposta e MANTÉM as revelações (contrato) — as
 * posições reveladas continuam fora da pontuação mesmo se o jogador as digitar de novo.
 */
export function clearRebusAnswer(solucao: string, answer: RebusAnswer): RebusAnswer {
  return { input: '', reveladas: normalizeReveladas(answer.reveladas, solucao.length) };
}
